import React, { useState, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import {
  FiCheckCircle,
  FiClock,
  FiSearch,
  FiFilter,
  FiMessageSquare,
  FiShield,
  FiCheckSquare,
  FiSquare,
  FiCalendar,
} from 'react-icons/fi'
import toast from 'react-hot-toast'
import { apiConnector } from '../../../../services/apiConnector'
import PayGlocalCheckoutModal from '../../../openhand/PayGlocalCheckoutModal'
import { LearnerScheduleSelectionModal } from '../../../openhand/LearnerScheduleSelectionModal'

export function Practitioners({ onUpdate, setActiveTab }) {

  const { token } = useSelector((state) => state.auth)
  const { user } = useSelector((state) => state.profile)

  const [practitioners, setPractitioners] = useState([])
  const [connections, setConnections] = useState([])
  const [loading, setLoading] = useState(true)
  const [payingId, setPayingId] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedSpecialty, setSelectedSpecialty] = useState('All')
  const [payglocalModalOpen, setPayglocalModalOpen] = useState(false)
  const [payglocalOrderData, setPayglocalOrderData] = useState(null)
  const [pendingPractitioner, setPendingPractitioner] = useState(null)

  // Scheduling states
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  const [schedulePractitioner, setSchedulePractitioner] = useState(null)
  const [scheduleOffer, setScheduleOffer] = useState(null)


  // Map of practitioner ID -> Array of selected offer IDs
  const [selectedOffersMap, setSelectedOffersMap] = useState({})

  // Load practitioners & connections from DB
  const loadData = useCallback(async () => {
    try {
      const resPract = await apiConnector('GET', '/api/v1/practitioners')
      if (resPract?.data?.success) {
        const list = resPract.data.practitioners || []
        setPractitioners(list)

        // Initialize default selected offer for each practitioner (default to first offer)
        const initialMap = {}
        list.forEach((p) => {
          const pId = p.user?._id || p._id
          const offers = (p.offers || p.userOffers || []).filter(
            (o) => (o.status === 'published' || (!o.status && o.status !== 'draft')) && !o.isDummy && o.price != null
          )
          if (offers.length > 0) {
            initialMap[pId] = [offers[0]._id]
          }
        })
        setSelectedOffersMap(initialMap)
      }

      if (token) {
        const resConn = await apiConnector(
          'GET',
          '/api/v1/practitioners/my-connections',
          null,
          { Authorization: `Bearer ${token}` }
        )
        if (resConn?.data?.success) {
          setConnections(resConn.data.connections || [])
        }
      }
    } catch (err) {
      console.error('Error loading practitioners data:', err)
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Handle return from PayGlocal hosted payment redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const status = params.get('status')
    const gid = params.get('gid') || params.get('x-gl-gid')
    const merchantTxnId = params.get('merchantTxnId') || params.get('orderId')
    const tokenParam = params.get('x-gl-token')

    if ((status === 'SUCCESS' || status === 'COMPLETED' || gid || tokenParam) && !params.get('verified')) {
      const pendingStr = localStorage.getItem('openhand_pending_booking')
      if (pendingStr) {
        try {
          const pending = JSON.parse(pendingStr)
          localStorage.removeItem('openhand_pending_booking')

          // Clean URL params to prevent duplicate verification
          window.history.replaceState({}, document.title, window.location.pathname)

          const confirmBooking = async () => {
            const vToast = toast.loading('Confirming PayGlocal payment & reserving session...')
            try {
              const res = await apiConnector(
                'POST',
                '/api/v1/practitioners/connect',
                {
                  practitionerId: pending.id,
                  amountPaid: pending.amount,
                  offerId: pending.offerId,
                  scheduledAt: pending.scheduledAt,
                  slotTime: pending.slotTime,
                  clientPhone: user?.whatsappNumber || user?.contactNumber || '',
                  clientEmail: user?.email || '',
                  payglocal_order_id: merchantTxnId,
                  payglocal_payment_id: gid,
                  payglocal_gid: gid,
                  token: tokenParam,
                },
                { Authorization: `Bearer ${token}` }
              )
              if (res?.data?.success) {
                toast.success(
                  `🎉 Session reserved for ${pending.slotTime || 'your chosen slot'}! Confirmation sent to your WhatsApp & Email.`,
                  { id: vToast, duration: 7000 }
                )
                loadData()
                if (onUpdate) onUpdate()
              } else {
                toast.error(res?.data?.message || 'Could not verify booking.', { id: vToast })
              }
            } catch (e) {
              console.error('Callback booking error:', e)
              toast.error('Booking confirmation notice: ' + (e.message || 'Complete'), { id: vToast })
            }
          }
          confirmBooking()
        } catch (_) {}
      }
    }
  }, [token, user, loadData, onUpdate])

  // Helper to get connection object for a practitioner
  const getConnectionForPractitioner = (practId) => {
    return connections.find(
      (c) => String(c.practitioner?._id || c.practitioner) === String(practId)
    )
  }

  // Toggle selection of an offer for a practitioner
  const toggleOfferSelection = (practId, offerId) => {
    setSelectedOffersMap((prev) => {
      const currentSelected = prev[practId] || []
      let updated = []
      if (currentSelected.includes(offerId)) {
        // Uncheck offer
        updated = currentSelected.filter((id) => id !== offerId)
      } else {
        // Check offer
        updated = [...currentSelected, offerId]
      }
      return { ...prev, [practId]: updated }
    })
  }

  // Calculate total fee for a practitioner based on selected checkboxes
  const getSelectedFeeForPractitioner = (p) => {
    const pId = p.user?._id || p._id
    const offers = (p.offers || p.userOffers || []).filter(
      (o) => (o.status === 'published' || (!o.status && o.status !== 'draft')) && !o.isDummy && o.price != null
    )
    const selectedIds = selectedOffersMap[pId] || []

    if (offers.length === 0) return 0

    const selectedOffers = offers.filter((o) => selectedIds.includes(o._id))
    if (selectedOffers.length === 0) return (offers[0]?.price || 0)

    return selectedOffers.reduce((sum, o) => sum + (o.price || 0), 0)
  }

  // Step 1: Learner clicks book session -> Opens interactive date and time slot calendar
  const handleOpenBookingSchedule = (practitioner) => {
    if (!token) {
      toast.error('Please login to select and connect with a practitioner')
      return
    }

    const pId = practitioner.user?._id || practitioner._id
    const offers = (practitioner.offers || practitioner.userOffers || []).filter(
      (o) => (o.status === 'published' || (!o.status && o.status !== 'draft')) && !o.isDummy && o.price != null
    )
    const selectedIds = selectedOffersMap[pId] || []
    const chosenOffer = offers.find((o) => selectedIds.includes(o._id)) || offers[0]

    if (!chosenOffer) {
      toast.error('Please select an offer to book')
      return
    }

    setSchedulePractitioner(practitioner)
    setScheduleOffer(chosenOffer)
    setShowScheduleModal(true)
  }

  // Step 2: Learner picks an available slot and confirms -> Opens PayGlocal checkout
  const handleSlotConfirmed = async (dateTimeString, slotInfo) => {
    setShowScheduleModal(false)
    if (!schedulePractitioner || !scheduleOffer) return

    await handlePayAndConnect(schedulePractitioner, scheduleOffer, {
      scheduledAt: slotInfo?.fullDateTime || dateTimeString,
      slotTime: slotInfo?.slotTime || '',
      date: slotInfo?.date || '',
    })
  }

  // Step 3: Initiate PayGlocal Checkout with practitioner, offer, and chosen scheduled slot
  const handlePayAndConnect = async (practitioner, chosenOffer, slotData = {}) => {
    if (!token) {
      toast.error('Please login to select and connect with a practitioner')
      return
    }

    const pId = practitioner.user?._id || practitioner._id
    const amount = chosenOffer?.price || getSelectedFeeForPractitioner(practitioner)
    const pName = `${practitioner.user?.firstName || practitioner.firstName || 'Practitioner'}`

    setPayingId(pId)

    try {
      // 1. Create PayGlocal order in backend
      const orderRes = await apiConnector(
        'POST',
        '/api/v1/payment/create-practitioner-order',
        {
          practitionerId: pId,
          amount,
          callbackUrl: `${window.location.origin}/app/practitioners`,
        },
        { Authorization: `Bearer ${token}` }
      )

      if (!orderRes?.data?.success) {
        toast.error(orderRes?.data?.message || 'Failed to initialize PayGlocal payment')
        setPayingId(null)
        return
      }

      const orderData = orderRes.data
      const orderObj = orderData.order || {}
      const finalAmount = orderData.amount || amount
      const liveTarget = orderData.redirectUrl || orderObj.redirectUrl

      const pendingData = {
        id: pId,
        name: pName,
        amount: finalAmount,
        offerId: chosenOffer?._id,
        scheduledAt: slotData.scheduledAt,
        slotTime: slotData.slotTime,
      }
      setPendingPractitioner(pendingData)
      try {
        localStorage.setItem('openhand_pending_booking', JSON.stringify(pendingData))
      } catch (_) {}

      // 2. Direct real-time redirect to official PayGlocal hosted checkout
      if (liveTarget && (liveTarget.startsWith('http://') || liveTarget.startsWith('https://'))) {
        toast.loading('Redirecting to PayGlocal Official Hosted Gateway...')
        window.location.href = liveTarget
        return
      }

      const planNameStr = slotData.slotTime
        ? `${chosenOffer?.title || 'Session'} • ${slotData.slotTime}`
        : `Counseling Fee for ${pName}`

      const modalOrder = {
        orderId: orderObj.id || orderObj.merchantTxnId || `txn_pgl_${Date.now()}`,
        gid: orderObj.gid || `GL_${Date.now()}`,
        redirectUrl: liveTarget,
        amount: finalAmount,
        currency: orderObj.currency || 'INR',
        planName: planNameStr,
        keyId: orderData.key,
        merchantId: orderObj.merchantId,
        customerData: {
          name: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '',
          email: user?.email || '',
          phone: user?.whatsappNumber || user?.additionalDetails?.contactNumber || user?.contactNumber || '',
        },
      }

      setPayglocalOrderData(modalOrder)
      setPayglocalModalOpen(true)
    } catch (err) {
      console.error('PayGlocal initiation error:', err)
      toast.error('Failed to initiate PayGlocal checkout. Please try again.')
    } finally {
      setPayingId(null)
    }
  }

  // Step 4: PayGlocal payment verified -> lock slot, notify via WhatsApp & Email to both Learner & Practitioner
  const handlePayGlocalSuccess = async (response) => {
    if (!pendingPractitioner) return
    const vToast = toast.loading('Reserving your slot and confirming booking...')
    try {
      const res = await apiConnector(
        'POST',
        '/api/v1/practitioners/connect',
        {
          practitionerId: pendingPractitioner.id,
          amountPaid: pendingPractitioner.amount,
          offerId: pendingPractitioner.offerId,
          scheduledAt: pendingPractitioner.scheduledAt,
          slotTime: pendingPractitioner.slotTime,
          clientPhone: user?.whatsappNumber || user?.contactNumber || user?.additionalDetails?.contactNumber || '',
          clientEmail: user?.email || '',
          payglocal_order_id: response.payglocal_order_id || response.merchantTxnId,
          payglocal_payment_id: response.payglocal_payment_id || response.gid,
          payglocal_gid: response.payglocal_gid || response.gid,
          payglocal_signature: response.signature,
        },
        { Authorization: `Bearer ${token}` }
      )

      if (res?.data?.success) {
        toast.success(
          `🎉 Session reserved for ${pendingPractitioner.slotTime || 'your slot'}! Confirmation sent to your WhatsApp & Email.`,
          { id: vToast, duration: 6000 }
        )
        loadData()
        if (onUpdate) onUpdate()
      } else {
        toast.error(res?.data?.message || 'Could not verify payment.', { id: vToast })
      }
    } catch (err) {
      console.error('Payment verification error:', err)
      toast.error('Connection request failed after payment verification.', { id: vToast })
    } finally {
      setPayglocalModalOpen(false)
      setPendingPractitioner(null)
    }
  }


  // Filtered practitioners list (ONLY display practitioners who have published at least 1 active offer!)
  const filteredPractitioners = practitioners.filter((p) => {
    const offers = (p.offers || p.userOffers || []).filter(
      (o) => o.status === 'published' || (!o.status && o.status !== 'draft')
    )
    if (offers.length === 0) return false // Hide practitioner card if practitioner has 0 active published offers

    const fullName = `${p.user?.firstName || p.firstName || ''} ${p.user?.lastName || p.lastName || ''}`.toLowerCase()
    const email = (p.user?.email || p.email || '').toLowerCase()
    const credentials = (p.credentials || '').toLowerCase()
    const bio = (p.bio || '').toLowerCase()
    const specialtiesList = (p.specialties || []).map((s) => s.toLowerCase())
    const specialtiesStr = specialtiesList.join(' ')
    const query = searchTerm.toLowerCase()

    const matchesSearch =
      fullName.includes(query) ||
      email.includes(query) ||
      credentials.includes(query) ||
      bio.includes(query) ||
      specialtiesStr.includes(query)

    if (!matchesSearch) return false

    if (selectedSpecialty === 'All' || selectedSpecialty === 'All Specialties') return true

    const categoryTarget = selectedSpecialty.toLowerCase()

    // Precise category matching
    const matchesCategory =
      specialtiesList.some((s) => {
        if (categoryTarget === 'cbt') return s.includes('cbt')
        if (categoryTarget === 'mindfulness') return s.includes('mindfulness')
        if (categoryTarget.includes('&')) {
          const parts = categoryTarget.split('&').map((part) => part.trim())
          return parts.some((part) => s.includes(part))
        }
        return s.includes(categoryTarget) || categoryTarget.includes(s)
      }) ||
      specialtiesStr.includes(categoryTarget.split(' ')[0])

    return matchesCategory
  })

  const specialtyCategories = [
    'All Specialties',
    'Emotional Intelligence',
    'Career Coaching',
    'Leadership Coaching',
    'Legal Advisors',
    'Finance Advisors',
    'Health & Fitness Coaches',
    'Relationship Coaching',
    'Parenting',
    'Spiritual',
    'NLP Coaching (Neurolinguistic Programming)',
  ]


  return (
    <div id="practitioners" style={{ width: '100%' }}>
      {/* Header */}
      <div className="hd" style={{ marginBottom: '24px' }}>
        <div style={{ fontSize: '11.5px', fontWeight: 800, textTransform: 'uppercase', color: '#8A2BE0', letterSpacing: '0.5px' }}>
          Registered Practitioners Directory
        </div>
        <h1 style={{ fontSize: '24px', fontWeight: 800, color: '#0F172A', margin: '4px 0' }}>
          Select Your Practitioner &amp; Book Session
        </h1>
        <p style={{ color: '#64748B', fontSize: '13.5px', margin: 0 }}>
          Browse verified practitioners, select your desired offer(s), pay the session fee, and connect directly.
        </p>
      </div>

      {/* Search Bar & Specialty Dropdown Filter Controls */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '24px', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '260px' }}>
          <FiSearch style={{ position: 'absolute', left: '14px', top: '13px', color: '#94A3B8' }} size={16} />
          <input
            type="text"
            placeholder="Search practitioner by name, email, credentials, or specialty..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              paddingLeft: '40px',
              paddingRight: '14px',
              paddingTop: '11px',
              paddingBottom: '11px',
              borderRadius: '12px',
              border: '1px solid #CBD5E1',
              fontSize: '14px',
              outline: 'none',
              background: '#FFFFFF',
              color: '#0F172A',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}
          />
        </div>

        <div style={{ position: 'relative', minWidth: '230px' }}>
          <FiFilter style={{ position: 'absolute', left: '14px', top: '13px', color: '#2563EB', pointerEvents: 'none' }} size={16} />
          <select
            value={selectedSpecialty}
            onChange={(e) => setSelectedSpecialty(e.target.value)}
            style={{
              width: '100%',
              paddingLeft: '40px',
              paddingRight: '36px',
              paddingTop: '11px',
              paddingBottom: '11px',
              borderRadius: '12px',
              border: '1px solid #CBD5E1',
              fontSize: '14px',
              fontWeight: 600,
              outline: 'none',
              background: '#FFFFFF',
              color: '#1E293B',
              cursor: 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              appearance: 'none',
              WebkitAppearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748B'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 12px center',
              backgroundSize: '16px 16px',
            }}
          >
            {specialtyCategories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Practitioners Directory Cards List */}
      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#64748B', fontSize: '14px' }}>
          Loading practitioners database...
        </div>
      ) : filteredPractitioners.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 items-start">
          {filteredPractitioners.map((p) => {
            const pId = p.user?._id || p._id
            const firstName = p.user?.firstName || p.firstName || 'Practitioner'
            const lastName = p.user?.lastName || p.lastName || ''
            const fullName = `${firstName} ${lastName}`.trim()
            const rawImg = p.user?.image || p.image
            const hasCustomImage = Boolean(
              rawImg &&
              typeof rawImg === 'string' &&
              rawImg.trim() !== '' &&
              !rawImg.includes('dicebear')
            )
            const firstLetter = (firstName.trim().slice(0, 1) || 'P').toUpperCase()

            const conn = getConnectionForPractitioner(pId)
            const connStatus = conn?.status // 'active' | 'approved' | 'pending_approval'
            const realOffers = (p.offers || p.userOffers || []).filter(
              (o) => (o.status === 'published' || (!o.status && o.status !== 'draft')) && !o.isDummy && o.price != null
            )
            const isDummyOnly = p.hasDummyOffer || realOffers.length === 0
            const offers = isDummyOnly ? (p.offers || p.userOffers || []) : realOffers

            const selectedIds = selectedOffersMap[pId] || []
            const selectedFee = getSelectedFeeForPractitioner(p)

            return (
              <div
                key={pId}
                style={{
                  background: '#FFFFFF',
                  borderRadius: '16px',
                  border: '1px solid #E2E8F0',
                  padding: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
                  position: 'relative',
                  alignSelf: 'start',
                  height: 'fit-content',
                }}
              >

                {/* Profile Header */}
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #1F5FE0 0%, #8A2BE0 100%)',
                      color: '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      fontSize: '18px',
                      flexShrink: 0,
                      overflow: 'hidden',
                      userSelect: 'none',
                    }}
                  >
                    {hasCustomImage ? (
                      <img
                        src={rawImg}
                        alt={fullName}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'
                          const fallback = e.currentTarget.nextElementSibling
                          if (fallback) fallback.style.display = 'flex'
                        }}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : null}
                    <span
                      style={{
                        display: hasCustomImage ? 'none' : 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                        lineHeight: 1,
                      }}
                    >
                      {firstLetter}
                    </span>
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {fullName}
                      </h3>
                      <span style={{ background: '#FEF3C7', color: '#92400E', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <FiShield size={10} /> Verified
                      </span>
                    </div>

                    <span style={{ fontSize: '12px', color: '#2563EB', fontWeight: 700, display: 'block' }}>
                      {p.credentials || 'Verified Clinical Practitioner'}
                    </span>
                  </div>
                </div>

                {/* Bio snippet */}
                <p style={{ margin: 0, fontSize: '13px', color: '#475569', lineHeight: 1.45 }}>
                  {p.bio || 'Certified practitioner specializing in holistic guidance, mental wellbeing, and learner growth.'}
                </p>

                {/* Specialty Tags */}
                {p.specialties && p.specialties.length > 0 && (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {p.specialties.map((spec, sIdx) => (
                      <button
                        key={sIdx}
                        type="button"
                        onClick={() => setSelectedSpecialty(spec)}
                        title={`Filter practitioners by ${spec}`}
                        style={{
                          background: selectedSpecialty === spec ? '#DBEAFE' : '#F1F5F9',
                          color: selectedSpecialty === spec ? '#1E40AF' : '#475569',
                          border: selectedSpecialty === spec ? '1px solid #BFDBFE' : '1px solid transparent',
                          padding: '3px 8px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        {spec}
                      </button>
                    ))}
                  </div>
                )}

                {/* Interactive Published Offers Section with Checkboxes */}
                {isDummyOnly ? (
                  <div style={{ background: '#F0FDF4', borderRadius: '12px', padding: '12px 14px', border: '1px dashed #86EFAC', textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '11.5px', fontWeight: 800, color: '#15803D', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                        🌱 1:1 Consultation (Upcoming)
                      </span>
                    </div>
                    <span style={{ fontSize: '12px', color: '#64748B', fontWeight: 600, display: 'block' }}>
                      Pricing &amp; booking coming soon • Price NA
                    </span>
                  </div>
                ) : offers.length > 0 ? (
                  <div style={{ background: '#F8FAFC', borderRadius: '12px', padding: '12px', border: '1px solid #E2E8F0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Published Offers &amp; Programs ({offers.length})
                      </span>
                      <span style={{ fontSize: '10.5px', color: '#64748B', fontStyle: 'italic' }}>
                        Click to select offer
                      </span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {offers.map((off, oIdx) => {
                        const isSelected = selectedIds.includes(off._id)
                        return (
                          <div
                            key={off._id || oIdx}
                            onClick={() => toggleOfferSelection(pId, off._id)}
                            style={{
                              background: isSelected ? '#EFF6FF' : '#FFFFFF',
                              border: isSelected ? '1.5px solid #2563EB' : '1px solid #CBD5E1',
                              borderRadius: '10px',
                              padding: '10px 12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                              boxShadow: isSelected ? '0 2px 8px rgba(37, 99, 235, 0.12)' : 'none',
                            }}
                          >
                            <div style={{ color: isSelected ? '#2563EB' : '#94A3B8', display: 'flex', alignItems: 'center' }}>
                              {isSelected ? <FiCheckSquare size={18} /> : <FiSquare size={18} />}
                            </div>

                            <div style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ fontSize: '13px', fontWeight: 800, color: '#0F172A', display: 'block' }}>
                                {off.title}
                              </span>
                              <span style={{ fontSize: '11px', color: '#64748B' }}>
                                {off.type === 'circle' ? 'Circle' : '1:1 Session'} • {off.durationMinutes || 50} mins
                              </span>
                            </div>

                            <span style={{ fontSize: '13.5px', fontWeight: 900, color: isSelected ? '#2563EB' : '#0F172A' }}>
                              ₹{off.price}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ) : (
                  <div style={{ background: '#F8FAFC', borderRadius: '12px', padding: '12px 16px', border: '1px dashed #CBD5E1', textAlign: 'center' }}>
                    <span style={{ fontSize: '12.5px', color: '#64748B', fontWeight: 600 }}>
                      No active offers published yet
                    </span>
                  </div>
                )}

                {/* Selected Fee Info (Only when real offers exist) */}
                {!isDummyOnly && offers.length > 0 && (
                  <div
                    style={{
                      paddingTop: '10px',
                      borderTop: '1px solid #F1F5F9',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '13px',
                    }}
                  >
                    <span style={{ fontWeight: 800, color: '#0F172A' }}>
                      Selected Fee:{' '}
                      <span style={{ color: '#2563EB', fontWeight: 900, fontSize: '15px' }}>₹{selectedFee}</span>
                    </span>

                    <span style={{ fontSize: '11px', color: '#64748B' }}>
                      PayGlocal Secured Checkout
                    </span>
                  </div>
                )}

                {/* Action Buttons based on Payment & Approval Status */}
                <div style={{ display: 'flex', gap: '8px', paddingTop: '4px' }}>
                  {connStatus === 'approved' || connStatus === 'active' ? (
                    <>
                      <button
                        disabled
                        style={{
                          flex: 1,
                          padding: '10px',
                          borderRadius: '10px',
                          background: '#F0FDF4',
                          border: '1px solid #BBF7D0',
                          color: '#166534',
                          fontWeight: 700,
                          fontSize: '12.5px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                        }}
                      >
                        <FiCheckCircle size={14} /> Approved &amp; Connected
                      </button>

                      <button
                        onClick={() => {
                          if (setActiveTab) setActiveTab('community')
                        }}
                        style={{
                          padding: '10px 14px',
                          borderRadius: '10px',
                          background: 'linear-gradient(135deg, #1F5FE0 0%, #8A2BE0 100%)',
                          color: '#FFFFFF',
                          border: 'none',
                          fontWeight: 700,
                          fontSize: '12.5px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                        title="Start Direct Chat"
                      >
                        <FiMessageSquare size={14} /> Chat
                      </button>
                    </>
                  ) : connStatus === 'pending_approval' ? (
                    <button
                      disabled
                      style={{
                        width: '100%',
                        padding: '11px',
                        borderRadius: '10px',
                        background: '#FEF3C7',
                        border: '1px solid #FDE68A',
                        color: '#92400E',
                        fontWeight: 700,
                        fontSize: '12.5px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                      }}
                    >
                      <FiClock size={15} /> Awaiting Practitioner Approval
                    </button>
                  ) : isDummyOnly ? (
                    <button
                      disabled
                      style={{
                        width: '100%',
                        padding: '11px',
                        borderRadius: '10px',
                        background: '#F1F5F9',
                        color: '#64748B',
                        border: '1px dashed #CBD5E1',
                        fontWeight: 700,
                        fontSize: '12.5px',
                        cursor: 'not-allowed',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      Pricing &amp; Booking Soon
                    </button>
                  ) : offers.length > 0 ? (
                    <button
                      onClick={() => handleOpenBookingSchedule(p)}
                      disabled={payingId === pId || selectedFee <= 0}
                      style={{
                        width: '100%',
                        padding: '11px',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #1F5FE0 0%, #8A2BE0 100%)',
                        color: '#FFFFFF',
                        border: 'none',
                        fontWeight: 700,
                        fontSize: '13px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        boxShadow: '0 4px 12px rgba(31, 95, 224, 0.25)',
                        transition: 'transform 0.15s ease',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
                      onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
                    >
                      <FiCalendar size={15} />
                      <span>
                        {payingId === pId ? 'Opening Calendar...' : `Book Session & Choose Slot (₹${selectedFee})`}
                      </span>
                    </button>
                  ) : (
                    <button
                      disabled
                      style={{
                        width: '100%',
                        padding: '11px',
                        borderRadius: '10px',
                        background: '#F1F5F9',
                        color: '#94A3B8',
                        border: '1px solid #E2E8F0',
                        fontWeight: 700,
                        fontSize: '12.5px',
                        cursor: 'not-allowed',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      No Active Offers Published
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div
          style={{
            padding: '40px',
            textAlign: 'center',
            background: '#FFFFFF',
            borderRadius: '16px',
            border: '1px solid #E2E8F0',
            color: '#64748B',
            fontSize: '14px',
          }}
        >
          No registered practitioners matched your search query. Try clearing filters.
        </div>
      )}

      {/* ── Interactive Date & Time Slot Calendar Modal ── */}
      <LearnerScheduleSelectionModal
        isOpen={showScheduleModal}
        onClose={() => {
          setShowScheduleModal(false)
          setSchedulePractitioner(null)
          setScheduleOffer(null)
        }}
        practitionerId={schedulePractitioner?.user?._id || schedulePractitioner?._id}
        practitionerName={schedulePractitioner?.user?.firstName || schedulePractitioner?.firstName}
        offer={scheduleOffer}
        onSlotSelected={handleSlotConfirmed}
      />

      {/* ── PayGlocal Checkout Modal ── */}
      {payglocalModalOpen && (
        <PayGlocalCheckoutModal
          isOpen={payglocalModalOpen}
          onClose={() => {
            setPayglocalModalOpen(false)
            setPayingId(null)
          }}
          orderData={payglocalOrderData}
          onSuccess={handlePayGlocalSuccess}
        />
      )}
    </div>
  )
}

export default Practitioners
